import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import { Box, Chip, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useState } from "react";
import {
  AddOnItemName,
  SubscriptionPortalFlow,
  SubscriptionType,
} from "schemas/dashboard";

import { getPageName } from "constants/trackingProps";
import CancelDialog from "features/Home/UpgradeDialog/Plan/CancelDialog";
import SubscriptionStatusText from "features/Home/UpgradeDialog/Plan/SubscriptionStatusText";
import { getStatusChipLabel } from "features/Home/UpgradeDialog/Plan/helpers";
import { fetcherAuth } from "utils/api";
import { SubscriptionColor } from "utils/color";
import { trackEvent } from "utils/tracking";

import CustomEmailBenefits from "./CustomEmailBenefits";
import ManageCustomEmailDialog from "./ManageCustomEmailDialog";

const styles = {
  chip: {
    color: "inherit",
    borderRadius: 2,
  },
};

const AddOnCustomEmail = () => {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const { subscription, setSubscription, addOns, setCustomDomainDialog } =
    useContext(SubscriptionContext);

  const [portalLoading, setPortalLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [openManageDialog, setOpenManageDialog] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);

  const backgroundColor = subscription?.customEmailData?.status
    ? SubscriptionColor[subscription?.customEmailData?.status]
    : "";
  const label = getStatusChipLabel(subscription?.customEmailData?.status);

  const getButtonName = (): {
    title: string;
    variant: "text" | "outlined" | "contained";
    action: () => void;
  } => {
    if (subscription && subscription?.customEmailData) {
      if (subscription?.customEmailData?.cancelAt) {
        return {
          title: "Renew",
          variant: "contained",
          action: handleRenew,
        };
      } else if (
        subscription?.customEmailData?.status === SubscriptionType.CANCELED
      ) {
        return {
          title: "Resubscribe",
          variant: "contained",
          action: handleCheckout,
        };
      } else if (
        subscription?.customEmailData?.status === SubscriptionType.INCOMPLETE
      ) {
        return {
          title: "Retry",
          variant: "contained",
          action: handleCheckout,
        };
      } else {
        return {
          title: "Manage Add-on",
          variant: "outlined",
          action: () => setOpenManageDialog(true),
        };
      }
    } else {
      return {
        title: "Purchase",
        variant: "contained",
        action: handleCheckout,
      };
    }
  };

  const handleCheckout = async () => {
    trackEvent("Set up a Professional Email Button Clicked", {
      Source: getPageName(),
      "Is Add On Section": true,
    });
    setCustomDomainDialog(true);
  };

  const handleManage = async (flow?: SubscriptionPortalFlow) => {
    try {
      setPortalLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments-add-ons/${subscription?.customEmailData?.id}/manage`,
        "POST",
        {},
        {
          flow,
        },
      );
      window.open(res.session.url, "_self");
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setPortalLoading(false);
    }
  };

  const handleRenew = async () => {
    setButtonLoading(true);
    try {
      setPortalLoading(true);
      const { addOnItem } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments-add-ons/${subscription?.customEmailData?.id}/renew`,
        "POST",
      );
      setAlert("Successfully renewed subscription", "success");
      if (addOnItem) {
        setSubscription((prev) =>
          prev
            ? {
                ...prev,
                customEmailData: addOnItem,
              }
            : prev,
        );
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setButtonLoading(false);
      setPortalLoading(false);
    }
  };

  const openCancel = () => {
    trackEvent(
      "Manage Subscription Dialog Cancel Subscription Button Pressed",
      {
        plan: AddOnItemName.custom_email,
      },
    );
    setOpenManageDialog(false);
    setOpenCancelDialog(true);
  };

  const closeCancelDialog = () => {
    trackEvent("Cancel Subscription Dialog Closed", {
      plan: AddOnItemName.custom_email,
    });
    setOpenCancelDialog(false);
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1, fontSize: 16 }} component="div">
        Professional Email Setup{" "}
        {label && (
          <Chip
            size="small"
            sx={[styles.chip, { backgroundColor }]}
            label={label}
          />
        )}{" "}
        <br />
        Custom Domain + $
        {addOns?.[AddOnItemName.custom_email]?.price.toFixed(2)}
        /month
      </Typography>
      <Box>
        Get a professional Gmail inbox with your own domain—fully set up by
        Bento. We'll monitor your spam score and step in if your account ever
        gets blocked.
        <CustomEmailBenefits />
      </Box>
      <SubscriptionStatusText
        status={
          subscription?.customEmailData?.status || SubscriptionType.TRIALING
        }
        cancelAt={subscription?.customEmailData?.cancelAt}
      />
      <LoadingButton
        onClick={getButtonName()?.action}
        disableElevation
        variant={getButtonName()?.variant}
        loading={buttonLoading}
      >
        {getButtonName()?.title}
      </LoadingButton>

      <ManageCustomEmailDialog
        open={openManageDialog}
        handleManagePlan={handleManage}
        loading={portalLoading}
        onClose={() => setOpenManageDialog(false)}
        openCancel={openCancel}
      />

      <CancelDialog
        loading={portalLoading}
        open={openCancelDialog}
        onClose={closeCancelDialog}
        warningText="You will lose access to your inbox and will lose access to your domain once the next billing cycle ends."
        handleManagePlan={handleManage}
        plan={AddOnItemName.custom_email}
      />
    </Box>
  );
};

export default AddOnCustomEmail;
